import dates_esp from "../translations/ES/dates.json";
import dates_eng from "../translations/EN/dates.json";
import dayjs from "dayjs";

const lang = localStorage.getItem("lang") || "es";
const language = lang === "es" ? dates_esp : dates_eng;

/**
 * "2023-11-27T10:40:50.681755", "2023-11-27 10:40:50", "2026/08/12 20:08:40" o
 * "2026/08/12" (sin zona). El separador de fecha puede ser "-" o "/": las
 * columnas de BD llegan con "-" (DateTimeField serializado por DRF) pero las
 * fechas de cambio de estado que viven dentro del JSON `status`
 * (status.assigned, history[].assigned y history[].updated_at) llegan con "/",
 * porque el backend las escribe con `strftime("%Y/%m/%d %H:%M:%S")`. Ambas
 * salen del mismo reloj. La hora es opcional para que "2026/08/12" no caiga al
 * parseo nativo, que lo tomaría como medianoche local y al este de UTC
 * mostraría el día anterior; con "-" el motor ya lo trata como UTC.
 */
const NAIVE_DATETIME =
  /^\d{4}[-/]\d{2}[-/]\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/;

/**
 * El backend entrega las fechas casi siempre sin zona (naive). Les agregamos
 * "Z" para clavarlas a un instante fijo: así el display no cambia según la zona
 * de cada usuario. De ahí se lee con getUTC*() para el display homogéneo o con
 * get*() para la hora local. Un Date, o un string que ya trae zona, se respetan.
 *
 * Sin esta normalización, parsear el string como local y releerlo con getHours()
 * se cancela solo... salvo en la hora que no existe al entrar el horario de
 * verano, donde el motor la corre hacia adelante y el display miente.
 *
 * OJO: esas fechas naive NO son UTC, son America/Chicago. `TIME_ZONE` y
 * `USE_TZ` están comentados en el settings de djaguar, así que Django cae a su
 * default y fuerza el proceso a esa zona. Mientras backend no migre, el sufijo
 * " UTC" y el tooltip de zona local mienten por 5 h (verano) o 6 h (invierno).
 * Al corregirse allá, este helper no cambia: sólo dejan de mentir los rótulos.
 */
const toUTCDate = (originDate) => {
  if (originDate instanceof Date) return new Date(originDate.getTime());
  const raw = String(originDate ?? "").trim();
  // El separador de fecha se normaliza a "-" ANTES de armar el Date: el formato
  // con "/" y "T" ("2026/08/12T20:08:40Z") no es parseable.
  if (NAIVE_DATETIME.test(raw)) {
    const iso = raw.replace(/\//g, "-").replace(" ", "T");
    // "YYYY-MM-DDZ" no está en la gramática de la spec: la medianoche va explícita.
    return new Date(iso.includes("T") ? `${iso}Z` : `${iso}T00:00:00Z`);
  }
  return new Date(raw);
};

// Date Formatter //
export const parseDatoToLgAndHZ = (originDate, locale = "es-MX") => {
  const fecha = new Date(originDate);
  const opcionesFecha = {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const opcionesHora = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    timeZoneName: "short",
  };
  const fechaFormateada = fecha.toLocaleDateString(locale, opcionesFecha);
  const horaFormateada = fecha.toLocaleTimeString(locale, opcionesHora);
  const resultado = `${fechaFormateada} - ${horaFormateada}`;
  return resultado;
};

/** @returns DD - Mon - YYYY */
export const parseDateToLgComun = (originDate, separator = " - ") => {
  const fecha = new Date(originDate);
  const meses = [
    `${language.mons.jan}`,
    `${language.mons.feb}`,
    `${language.mons.mar}`,
    `${language.mons.apr}`,
    `${language.mons.may}`,
    `${language.mons.jun}`,
    `${language.mons.jul}`,
    `${language.mons.aug}`,
    `${language.mons.sep}`,
    `${language.mons.oct}`,
    `${language.mons.nov}`,
    `${language.mons.dec}`,
  ];
  const mes = meses[fecha.getMonth()];
  const resultado = `${fecha.getDate()}${separator}${mes}${separator}${fecha.getFullYear()}`;
  return resultado;
};

/** @returns ENG = "Mont DD, YYYY" || ESP = "DD de Mont del YYYY" */
export const parseDateToLgComun2 = (originalDate) => {
  const fecha = new Date(originalDate);
  const ano = fecha.getFullYear();
  const meses = [
    `${language.monts.january}`,
    `${language.monts.february}`,
    `${language.monts.march}`,
    `${language.monts.april}`,
    `${language.monts.may}`,
    `${language.monts.june}`,
    `${language.monts.july}`,
    `${language.monts.august}`,
    `${language.monts.september}`,
    `${language.monts.october}`,
    `${language.monts.november}`,
    `${language.monts.december}`,
  ];
  const mes = meses[fecha.getMonth()];
  const result = `${mes} ${fecha.getDate()}, ${ano}`;
  const resultado = `${fecha.getDate()} de ${mes} ${
    ano >= 2000 ? "del" : "de"
  } ${ano}`;
  if (lang === "es") {
    return resultado;
  } else {
    return result;
  }
};

/** @return ENG = "Day, DD MM YYYY - 00:00AM/PM" || ESP = "Día, DD de MM del YYYY - 00:00am/pm */
export const parseDateToLgComun3 = (
  originalDate,
  lang = "es",
  hoursToAdjust = 0,
  showTime = true
) => {
  //const date = new Date(originalDate);
  const date = new Date(originalDate);
  if (hoursToAdjust !== 0) {
    date.setHours(date.getHours() + hoursToAdjust);
  }

  const days = [
    `${language.days.sun}`,
    `${language.days.mon}`,
    `${language.days.tue}`,
    `${language.days.wed}`,
    `${language.days.thu}`,
    `${language.days.fri}`,
    `${language.days.sat}`,
  ];
  const weekDay = days[date.getDay()];
  const mons = [
    `${language.mons.jan}`,
    `${language.mons.feb}`,
    `${language.mons.mar}`,
    `${language.mons.apr}`,
    `${language.mons.may}`,
    `${language.mons.jun}`,
    `${language.mons.jul}`,
    `${language.mons.aug}`,
    `${language.mons.sep}`,
    `${language.mons.oct}`,
    `${language.mons.nov}`,
    `${language.mons.dec}`,
  ];
  const mon = mons[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  let hour = date.getHours();
  const minutes = date.getMinutes();
  const periodo = hour >= 12 ? "p.m." : "a.m.";
  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  // La hora es opcional (showTime); útil para fechas de solo día.
  const timeEs = showTime
    ? ` - ${hour}:${minutes < 10 ? "0" : ""}${minutes}${periodo}`
    : "";
  const timeEn = showTime
    ? ` - ${hour}:${minutes < 10 ? "0" : ""}${minutes}${period}`
    : "";
  const resultado = `${weekDay}, ${day} de ${mon} ${
    year >= 2000 ? "del" : "de"
  } ${year}${timeEs}`;
  const result = `${weekDay}, ${day} ${mon} ${year}${timeEn}`;

  if (lang === "es") {
    return resultado;
  } else {
    return result;
  }
};

/** @return ENG = "Day, DD MM YYYY - 00:00:00 hrs" || ESP = "Día, DD de MM del YYYY - 00:00:00 hrs */

export const parseDateToLgComun4 = (
  originalDate,
  lang = "es",
  showUTC = false
) => {
  const date = toUTCDate(originalDate);
  if (isNaN(date.getTime())) return "";

  const days = [
    `${language.days.sun}`,
    `${language.days.mon}`,
    `${language.days.tue}`,
    `${language.days.wed}`,
    `${language.days.thu}`,
    `${language.days.fri}`,
    `${language.days.sat}`,
  ];
  const weekDay = days[date.getUTCDay()];
  const mons = [
    `${language.mons.jan}`,
    `${language.mons.feb}`,
    `${language.mons.mar}`,
    `${language.mons.apr}`,
    `${language.mons.may}`,
    `${language.mons.jun}`,
    `${language.mons.jul}`,
    `${language.mons.aug}`,
    `${language.mons.sep}`,
    `${language.mons.oct}`,
    `${language.mons.nov}`,
    `${language.mons.dec}`,
  ];
  const mon = mons[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  // Formatear hora en formato de 24 horas
  const hour = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds(); // Agregar los segundos

  const resultado = `${weekDay}, ${day} de ${mon} ${
    year >= 2000 ? "del" : "de"
  } ${year} - ${hour < 10 ? "0" : ""}${hour}:${
    minutes < 10 ? "0" : ""
  }${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;

  const result = `${weekDay}, ${day} ${mon} ${year} - ${
    hour < 10 ? "0" : ""
  }${hour}:${minutes < 10 ? "0" : ""}${minutes}:${
    seconds < 10 ? "0" : ""
  }${seconds}`;

  // Rótulo pendiente de backend: hoy la hora es America/Chicago, no UTC.
  const utcSuffix = showUTC ? " UTC" : "";

  if (lang === "es") {
    return `${resultado}${utcSuffix}`;
  } else {
    return `${result}${utcSuffix}`;
  }
};

/**
 * Convierte una fecha del backend (naive) a la zona del navegador, para el
 * tooltip. El cálculo asume que la naive es UTC, así que hoy sale corrida:
 * ver la nota de `toUTCDate`. Se corrige cuando backend migre a UTC.
 * @returns "En tu zona horaria: DD/MM/YYYY HH:MM:SS" (o "" si la fecha es inválida)
 */
export const parseDateToLocalTZ = (originDate) => {
  if (!originDate) return "";

  // Verificamos que el navegador exponga su zona horaria; si no, avisamos.
  let timeZone;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = null;
  }
  if (!timeZone) return language.tooltip.no_tz;

  // Mismo instante que muestra el display; aquí se lee con get*() (hora local).
  const fecha = toUTCDate(originDate);
  if (isNaN(fecha.getTime())) return "";
  const day = String(fecha.getDate()).padStart(2, "0");
  const month = String(fecha.getMonth() + 1).padStart(2, "0");
  const year = fecha.getFullYear();
  const hours = String(fecha.getHours()).padStart(2, "0");
  const minutes = String(fecha.getMinutes()).padStart(2, "0");
  const seconds = String(fecha.getSeconds()).padStart(2, "0");
  return `${language.tooltip.in_your_tz}: ${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

/** @returns YYYY-MM-DD */
export const parseDateToYYYYMMDD = (originDate) => {
  if (!validInitDates(originDate)) return;
  const fecha = new Date(originDate);
  const anio = fecha.getFullYear().toString();
  const mes = ("0" + (fecha.getMonth() + 1)).slice(-2);
  const dia = ("0" + fecha.getDate()).slice(-2);
  const fechaFormateada = `${anio}-${mes}-${dia}`;
  return fechaFormateada;
};

/** @returns DD-MM-YYYY */
export const parseDateToDDMMYYYY = (originDate) => {
  if (!validInitDates(originDate)) return;
  const fecha = new Date(originDate);
  const anio = fecha.getFullYear().toString();
  const mes = ("0" + (fecha.getMonth() + 1)).slice(-2);
  const dia = ("0" + fecha.getDate()).slice(-2);
  const fechaFormateada = `${dia}-${mes}-${anio}`;
  return fechaFormateada;
};

/** @returns DD/MM/YYYY */
export const parseDateToDDMMYYYY2 = (originDate) => {
  if (!validInitDates(originDate)) return;
  const fecha = new Date(originDate);
  const anio = fecha.getFullYear().toString();
  const mes = ("0" + (fecha.getMonth() + 1)).slice(-2);
  const dia = ("0" + fecha.getDate()).slice(-2);
  const fechaFormateada = `${dia}/${mes}/${anio}`;
  return fechaFormateada;
};

export const validInitDates = (date) => {
  var fechaValida = Date.parse(date);
  return !isNaN(fechaValida);
};

export const transformDayJs = (input) => {
  const date = dayjs(input);
  return date.isValid() ? date : null; // Devuelve un Date o null si no es válido
}

/** @returns YYYY/MM/DD */
export const parseDateToYYYYMMDD2 = (originDate) => {
  const fecha = new Date(originDate);
  const anio = fecha.getFullYear().toString();
  const mes = ("0" + (fecha.getMonth() + 1)).slice(-2);
  const dia = ("0" + fecha.getDate()).slice(-2);
  const fechaFormateada = `${anio}/${mes}/${dia}`;
  return fechaFormateada;
};

/**
 * Camino de escritura: serializa un día de calendario para mandarlo al API.
 * No sirve para mostrar; el resto de este archivo son helpers de display.
 *
 * conclusion_at no es un instante sino un día: el usuario lo elige en un
 * date picker. Se manda como medianoche UTC para que el backend devuelva el
 * mismo día que se eligió, ya que la UI muestra las fechas en UTC. Mandar el
 * Date crudo serializa la medianoche local, que al este de UTC cae en el día
 * anterior.
 *
 * Un string naive (sin zona) se guardaría corrido: el backend corre en
 * America/Chicago con USE_TZ=False y lo interpreta como hora del servidor.
 *
 * @param originDate Date, dayjs o string
 * @returns "YYYY-MM-DDT00:00:00Z", o null si la fecha no es válida
 */
export const serializeDayForAPI = (originDate) => {
  if (!originDate) return null;
  const date = dayjs(originDate);
  return date.isValid() ? `${date.format("YYYY-MM-DD")}T00:00:00Z` : null;
};

/** @returns DD-MM-YYYY 00:00:00 */
export const parseDateToISOString2 = (originDate, showUTC = false) => {
  const fecha = toUTCDate(originDate);
  if (isNaN(fecha.getTime())) return "";
  const year = fecha.getUTCFullYear();
  const month = String(fecha.getUTCMonth() + 1).padStart(2, "0");
  const day = String(fecha.getUTCDate()).padStart(2, "0");
  const hours = String(fecha.getUTCHours()).padStart(2, "0");
  const minutes = String(fecha.getUTCMinutes()).padStart(2, "0");
  const seconds = String(fecha.getUTCSeconds()).padStart(2, "0");
  // Rótulo pendiente de backend: hoy la hora es America/Chicago, no UTC.
  const utcSuffix = showUTC ? " UTC" : "";
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}${utcSuffix}`;
};

/** @returns DD/MM/YYYY 00:00 hrs. */
export const parseDateToISOString4 = (originDate, showUTC = false) => {
  const fecha = toUTCDate(originDate);
  if (isNaN(fecha.getTime())) return "";
  const year = fecha.getUTCFullYear();
  const month = String(fecha.getUTCMonth() + 1).padStart(2, "0");
  const day = String(fecha.getUTCDate()).padStart(2, "0");
  const hours = String(fecha.getUTCHours()).padStart(2, "0");
  const minutes = String(fecha.getUTCMinutes()).padStart(2, "0");
  // Rótulo pendiente de backend: hoy la hora es America/Chicago, no UTC.
  const utcSuffix = showUTC ? " UTC" : "";
  return `${day}/${month}/${year} ${hours}:${minutes} hrs${utcSuffix}`;
};
